/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import {
  ArrowFunction,
  AST,
  AstVisitor,
  ASTWithSource,
  Binary,
  BindingPipe,
  Call,
  Chain,
  Conditional,
  EmptyExpr,
  ImplicitReceiver,
  Interpolation,
  KeyedRead,
  LiteralArray,
  LiteralMap,
  LiteralPrimitive,
  NonNullAssert,
  ParenthesizedExpression,
  PrefixNot,
  PropertyRead,
  RegularExpressionLiteral,
  SafeCall,
  SafeKeyedRead,
  SafePropertyRead,
  SpreadElement,
  TaggedTemplateLiteral,
  TemplateLiteral,
  ThisReceiver,
  TypeofExpression,
  Unary,
  VoidExpression,
} from '../expression_parser/ast';
import {TypeCheckingConfig} from './api';
import {TcbExpr} from './ops/codegen';

/**
 * Convert an `AST` to a `TcbExpr` directly, without going through an intermediate `Expression`
 * AST.
 */
export function astToTcbExpr(
  ast: AST,
  maybeResolve: (ast: AST) => TcbExpr | null,
  config: TypeCheckingConfig,
): TcbExpr {
  const translator = new TcbExprTranslator(maybeResolve, config);
  return translator.translate(ast);
}

class TcbExprTranslator implements AstVisitor {
  constructor(
    private maybeResolve: (ast: AST) => TcbExpr | null,
    private config: TypeCheckingConfig,
  ) {}

  translate(ast: AST): TcbExpr {
    if (ast instanceof ASTWithSource) {
      ast = ast.ast;
    }

    // First attempt to let any custom resolution logic provide a translation for the given node.
    const resolved = this.maybeResolve(ast);
    if (resolved !== null) {
      return resolved;
    }

    return ast.visit(this);
  }

  visitUnary(ast: Unary): TcbExpr {
    const expr = this.translate(ast.expr);
    const node = new TcbExpr(`${ast.operator}${expr.print()}`);
    return node.wrapForTypeChecker().addParseSpanInfo(ast.sourceSpan);
  }

  visitBinary(ast: Binary): TcbExpr {
    const lhs = this.translate(ast.left);
    const rhs = this.translate(ast.right);
    lhs.wrapForTypeChecker();
    rhs.wrapForTypeChecker();
    const expression = `${lhs.print()} ${ast.operation} ${rhs.print()}`;
    const node = new TcbExpr(
      ast.operation === '??' || ast.operation === '**' ? `(${expression})` : expression,
    );
    node.addParseSpanInfo(ast.sourceSpan);
    return node;
  }

  visitChain(ast: Chain): TcbExpr {
    const elements = ast.expressions.map((expr) => this.translate(expr).print());
    const node = new TcbExpr(elements.join(', '));
    node.wrapForTypeChecker();
    node.addParseSpanInfo(ast.sourceSpan);
    return node;
  }

  visitConditional(ast: Conditional): TcbExpr {
    const condExpr = this.translate(ast.condition);
    const trueExpr = this.translate(ast.trueExp);
    // Wrap `falseExpr` in parens so that the trailing parse span info is not attributed to the
    // whole conditional.
    // In the following example, the last source span comment (5,6) could be seen as the
    // trailing comment for _either_ the whole conditional expression _or_ just the `falseExpr` that
    // is immediately before it:
    // `conditional /*1,2*/ ? trueExpr /*3,4*/ : falseExpr /*5,6*/`
    // This should be instead be `conditional /*1,2*/ ? trueExpr /*3,4*/ : (falseExpr /*5,6*/)`
    const falseExpr = this.translate(ast.falseExp).wrapForTypeChecker();
    const node = new TcbExpr(
      `(${condExpr.print()} ? ${trueExpr.print()} : ${falseExpr.print()})`,
    ).addParseSpanInfo(ast.sourceSpan);
    return node;
  }

  visitImplicitReceiver(ast: ImplicitReceiver): never {
    throw new Error('Method not implemented.');
  }

  visitThisReceiver(ast: ThisReceiver): never {
    throw new Error('Method not implemented.');
  }

  visitRegularExpressionLiteral(ast: RegularExpressionLiteral, context: any): TcbExpr {
    const node = new TcbExpr(`/${ast.body}/${ast.flags ?? ''}`);
    node.wrapForTypeChecker();
    return node;
  }

  visitInterpolation(ast: Interpolation): TcbExpr {
    const exprs = ast.expressions.map((e) => {
      const node = this.translate(e);
      node.wrapForTypeChecker();
      return node.print();
    });
    return new TcbExpr(`"" + ${exprs.join(' + ')}`);
  }

  visitKeyedRead(ast: KeyedRead): TcbExpr {
    const receiver = this.translate(ast.receiver);
    if (!this.isStrictSafeNavigationChain(ast.receiver)) {
      receiver.wrapForTypeChecker();
    }
    const key = this.translate(ast.key);
    return new TcbExpr(`${receiver.print()}[${key.print()}]`).addParseSpanInfo(ast.sourceSpan);
  }

  visitLiteralArray(ast: LiteralArray): TcbExpr {
    const elements = ast.expressions.map((expr) => this.translate(expr));
    let literal = `[${elements.map((el) => el.print()).join(', ')}]`;

    if (!this.config.strictLiteralTypes) {
      literal = `(${literal} as any)`;
    }

    return new TcbExpr(literal).addParseSpanInfo(ast.sourceSpan);
  }

  visitLiteralMap(ast: LiteralMap): TcbExpr {
    const properties = ast.keys.map((key, idx) => {
      const value = this.translate(ast.values[idx]);

      if (key.kind === 'property') {
        const keyNode = new TcbExpr(TcbExpr.quoteAndEscape(key.key));
        keyNode.addParseSpanInfo(key.sourceSpan);
        return `${keyNode.print()}: ${value.print()}`;
      } else {
        return `...${value.print()}`;
      }
    });

    let literal = `{ ${properties.join(', ')} }`;

    if (!this.config.strictLiteralTypes) {
      literal = `${literal} as any`;
    }

    const expression = new TcbExpr(literal).addParseSpanInfo(ast.sourceSpan);
    expression.wrapForTypeChecker();

    return expression;
  }

  visitLiteralPrimitive(ast: LiteralPrimitive): TcbExpr {
    let node: TcbExpr;
    if (ast.value === undefined) {
      node = new TcbExpr('undefined');
    } else if (ast.value === null) {
      node = new TcbExpr('null');
    } else if (typeof ast.value === 'string') {
      node = new TcbExpr(TcbExpr.quoteAndEscape(ast.value));
    } else if (typeof ast.value === 'number') {
      if (Number.isNaN(ast.value)) {
        node = new TcbExpr('NaN');
      } else if (!Number.isFinite(ast.value)) {
        node = new TcbExpr(ast.value > 0 ? 'Infinity' : '-Infinity');
      } else {
        node = new TcbExpr(ast.value.toString());
      }
    } else if (typeof ast.value === 'boolean') {
      node = new TcbExpr(ast.value + '');
    } else {
      throw Error(`Unsupported AST value of type ${typeof ast.value}`);
    }
    node.addParseSpanInfo(ast.sourceSpan);
    return node;
  }

  visitNonNullAssert(ast: NonNullAssert): TcbExpr {
    const expr = this.translate(ast.expression).wrapForTypeChecker();
    return new TcbExpr(`${expr.print()}!`).addParseSpanInfo(ast.sourceSpan);
  }

  visitPipe(ast: BindingPipe): never {
    throw new Error('Method not implemented.');
  }

  visitPrefixNot(ast: PrefixNot): TcbExpr {
    const expression = this.translate(ast.expression).wrapForTypeChecker();
    return new TcbExpr(`!${expression.print()}`).addParseSpanInfo(ast.sourceSpan);
  }

  visitTypeofExpression(ast: TypeofExpression): TcbExpr {
    const expression = this.translate(ast.expression).wrapForTypeChecker();
    return new TcbExpr(`typeof ${expression.print()}`).addParseSpanInfo(ast.sourceSpan);
  }

  visitVoidExpression(ast: VoidExpression): TcbExpr {
    const expression = this.translate(ast.expression).wrapForTypeChecker();
    return new TcbExpr(`void ${expression.print()}`).addParseSpanInfo(ast.sourceSpan);
  }

  visitPropertyRead(ast: PropertyRead): TcbExpr {
    const receiver = this.translate(ast.receiver);
    const isSafeChain = this.isStrictSafeNavigationChain(ast.receiver);
    if (!isSafeChain) {
      receiver.wrapForTypeChecker();
    }

    const node = new TcbExpr(`${receiver.print()}.${ast.name}`).addParseSpanInfo(ast.nameSpan);

    let result: TcbExpr;
    if (isSafeChain) {
      result = new TcbExpr(node.print());
    } else {
      result = node.wrapForTypeChecker();
    }
    return result.addParseSpanInfo(ast.sourceSpan);
  }

  visitSafePropertyRead(ast: SafePropertyRead): TcbExpr {
    let node: TcbExpr;
    const receiver = this.translate(ast.receiver).wrapForTypeChecker();
    const name = new TcbExpr(ast.name).addParseSpanInfo(ast.nameSpan);

    if (this.config.strictSafeNavigationTypes) {
      node = new TcbExpr(`${receiver.print()}?.${name.print()}`);
    } else if (VeSafeLhsInferenceBugDetector.veWillInferAnyFor(ast)) {
      node = new TcbExpr(`(${receiver.print()} as any).${name.print()}`);
    } else {
      node = new TcbExpr(`(${receiver.print()}!.${name.print()} as any)`);
    }
    return node.addParseSpanInfo(ast.sourceSpan);
  }

  visitSafeKeyedRead(ast: SafeKeyedRead): TcbExpr {
    const receiver = this.translate(ast.receiver).wrapForTypeChecker();
    const key = this.translate(ast.key);
    let node: TcbExpr;

    if (this.config.strictSafeNavigationTypes) {
      node = new TcbExpr(`${receiver.print()}?.[${key.print()}]`);
    } else if (VeSafeLhsInferenceBugDetector.veWillInferAnyFor(ast)) {
      node = new TcbExpr(`(${receiver.print()} as any)[${key.print()}]`);
    } else {
      const elementAccess = new TcbExpr(`${receiver.print()}![${key.print()}]`).addParseSpanInfo(
        ast.sourceSpan,
      );
      node = new TcbExpr(`(${elementAccess.print()} as any)`);
    }
    return node.addParseSpanInfo(ast.sourceSpan);
  }

  visitCall(ast: Call): TcbExpr {
    const args = ast.args.map((expr) => this.translate(expr));
    const receiver = ast.receiver;
    let expr: TcbExpr;

    if (receiver instanceof PropertyRead) {
      const resolved = this.maybeResolve(receiver);
      if (resolved !== null) {
        expr = resolved;
      } else {
        const propertyReceiver = this.translate(receiver.receiver);
        if (!this.isStrictSafeNavigationChain(receiver.receiver)) {
          propertyReceiver.wrapForTypeChecker();
        }
        expr = new TcbExpr(`${propertyReceiver.print()}.${receiver.name}`).addParseSpanInfo(
          receiver.nameSpan,
        );
      }
    } else {
      expr = this.translate(receiver);
    }

    let node: TcbExpr;

    if (ast.receiver instanceof SafePropertyRead || ast.receiver instanceof SafeKeyedRead) {
      node = this.convertToSafeCall(ast, expr, args);
    } else {
      node = new TcbExpr(`${expr.print()}(${args.map((arg) => arg.print()).join(', ')})`);
    }

    return node.addParseSpanInfo(ast.sourceSpan);
  }

  visitSafeCall(ast: SafeCall): TcbExpr {
    const args = ast.args.map((expr) => this.translate(expr));
    const expr = this.translate(ast.receiver).wrapForTypeChecker();
    return this.convertToSafeCall(ast, expr, args).addParseSpanInfo(ast.sourceSpan);
  }

  visitTemplateLiteral(ast: TemplateLiteral): TcbExpr {
    const length = ast.elements.length;
    const head = ast.elements[0];
    let result: string;

    if (length === 1) {
      result = `\`${this.escapeTemplateLiteral(head.text)}\``;
    } else {
      let parts = [`\`${this.escapeTemplateLiteral(head.text)}`];
      const tailIndex = length - 1;

      for (let i = 1; i < tailIndex; i++) {
        const expr = this.translate(ast.expressions[i - 1]);
        parts.push(`\${${expr.print()}}${this.escapeTemplateLiteral(ast.elements[i].text)}`);
      }
      const resolvedExpression = this.translate(ast.expressions[tailIndex - 1]);
      parts.push(
        `\${${resolvedExpression.print()}}${this.escapeTemplateLiteral(ast.elements[tailIndex].text)}\``,
      );
      result = parts.join('');
    }
    return new TcbExpr(result);
  }

  visitTemplateLiteralElement() {
    throw new Error('Method not implemented');
  }

  visitTaggedTemplateLiteral(ast: TaggedTemplateLiteral): TcbExpr {
    const tag = this.translate(ast.tag);
    const template = this.visitTemplateLiteral(ast.template);
    return new TcbExpr(`${tag.print()}${template.print()}`);
  }

  visitParenthesizedExpression(ast: ParenthesizedExpression): TcbExpr {
    const expr = this.translate(ast.expression);
    return new TcbExpr(`(${expr.print()})`);
  }

  visitSpreadElement(ast: SpreadElement) {
    const expression = this.translate(ast.expression);
    expression.wrapForTypeChecker();
    const node = new TcbExpr(`...${expression.print()}`);
    node.addParseSpanInfo(ast.sourceSpan);
    return node;
  }

  visitEmptyExpr(ast: EmptyExpr) {
    const node = new TcbExpr('undefined');
    node.addParseSpanInfo(ast.sourceSpan);
    return node;
  }

  visitArrowFunction(ast: ArrowFunction): TcbExpr {
    const params = ast.parameters
      .map((param) => new TcbExpr(param.name).markIgnoreDiagnostics().print())
      .join(', ');
    const body = astToTcbExpr(
      ast.body,
      (innerAst) => {
        if (
          !(innerAst instanceof PropertyRead) ||
          innerAst.receiver instanceof ThisReceiver ||
          !(innerAst.receiver instanceof ImplicitReceiver)
        ) {
          return this.maybeResolve(innerAst);
        }

        const correspondingParam = ast.parameters.find((arg) => arg.name === innerAst.name);

        if (correspondingParam) {
          const node = new TcbExpr(innerAst.name);
          node.addParseSpanInfo(innerAst.sourceSpan);
          return node;
        }

        return this.maybeResolve(innerAst);
      },
      this.config,
    );

    return new TcbExpr(
      `${ast.parameters.length === 1 ? params : `(${params})`} => ${body.print()}`,
    );
  }

  private convertToSafeCall(ast: Call | SafeCall, exprNode: TcbExpr, argNodes: TcbExpr[]): TcbExpr {
    const expr = exprNode.print();
    const args = argNodes.map((node) => node.print()).join(', ');

    if (this.config.strictSafeNavigationTypes) {
      return new TcbExpr(`${expr}?.(${args})`);
    }

    if (VeSafeLhsInferenceBugDetector.veWillInferAnyFor(ast)) {
      return new TcbExpr(`(${expr} as any)(${args})`);
    }

    return new TcbExpr(`(${expr}!(${args}) as any)`);
  }

  private escapeTemplateLiteral(value: string) {
    return value.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\${/g, '$\\{');
  }

  private isStrictSafeNavigationChain(ast: AST): boolean {
    if (!this.config.strictSafeNavigationTypes) {
      return false;
    }
    let current: AST | undefined = ast;
    while (current) {
      if (
        current instanceof SafePropertyRead ||
        current instanceof SafeKeyedRead ||
        current instanceof SafeCall
      ) {
        return true;
      }
      if (
        current instanceof PropertyRead ||
        current instanceof KeyedRead ||
        current instanceof Call
      ) {
        current = current.receiver;
      } else if (current instanceof NonNullAssert) {
        current = current.expression;
      } else {
        break;
      }
    }
    return false;
  }
}

class VeSafeLhsInferenceBugDetector implements AstVisitor {
  private static SINGLETON = new VeSafeLhsInferenceBugDetector();

  static veWillInferAnyFor(ast: Call | SafeCall | SafePropertyRead | SafeKeyedRead) {
    const visitor = VeSafeLhsInferenceBugDetector.SINGLETON;
    return ast instanceof Call ? ast.visit(visitor) : ast.receiver.visit(visitor);
  }

  visitUnary(ast: Unary): boolean {
    return ast.expr.visit(this);
  }
  visitBinary(ast: Binary): boolean {
    return ast.left.visit(this) || ast.right.visit(this);
  }
  visitChain(): boolean {
    return false;
  }
  visitConditional(ast: Conditional): boolean {
    return ast.condition.visit(this) || ast.trueExp.visit(this) || ast.falseExp.visit(this);
  }
  visitCall(): boolean {
    return true;
  }
  visitSafeCall(): boolean {
    return false;
  }
  visitImplicitReceiver(): boolean {
    return false;
  }
  visitThisReceiver(): boolean {
    return false;
  }
  visitInterpolation(ast: Interpolation): boolean {
    return ast.expressions.some((exp) => exp.visit(this));
  }
  visitKeyedRead(): boolean {
    return false;
  }
  visitLiteralArray(): boolean {
    return true;
  }
  visitLiteralMap(): boolean {
    return true;
  }
  visitLiteralPrimitive(): boolean {
    return false;
  }
  visitPipe(): boolean {
    return true;
  }
  visitPrefixNot(ast: PrefixNot): boolean {
    return ast.expression.visit(this);
  }
  visitTypeofExpression(ast: TypeofExpression): boolean {
    return ast.expression.visit(this);
  }
  visitVoidExpression(ast: VoidExpression): boolean {
    return ast.expression.visit(this);
  }
  visitNonNullAssert(ast: NonNullAssert): boolean {
    return ast.expression.visit(this);
  }
  visitPropertyRead(): boolean {
    return false;
  }
  visitSafePropertyRead(): boolean {
    return false;
  }
  visitSafeKeyedRead(): boolean {
    return false;
  }
  visitTemplateLiteral() {
    return false;
  }
  visitTemplateLiteralElement() {
    return false;
  }
  visitTaggedTemplateLiteral() {
    return false;
  }
  visitParenthesizedExpression(ast: ParenthesizedExpression) {
    return ast.expression.visit(this);
  }
  visitRegularExpressionLiteral() {
    return false;
  }
  visitSpreadElement(ast: SpreadElement) {
    return ast.expression.visit(this);
  }
  visitArrowFunction(ast: ArrowFunction, context: any) {
    return false;
  }
}
